// Mappers for Shift domain: transport DTOs ↔ UI models
import { formatRut, formatToDdMmYyyy, toHHmm } from '@/utils';
import type {
  LC_GetShiftRequestsOut,
  LC_ShiftRequestOut,
  ShiftRequest,
  ShiftRequestPage,
  ShiftRequestFormData,
  LC_SubmitShiftChangeRequestIn,
  LC_CompanyShiftRequestItem,
  LC_GetCompanyShiftRequestsOut,
  LC_GetCompanyShiftRequestCountersOut,
  CompanyShiftRequest,
  CompanyShiftRequestPage,
  ShiftRequestCounters,
  ShiftRequestStatus,
  LC_CompanyShiftItem,
  LC_GetCompanyShiftsListOut,
  CompanyShiftCatalogItem,
  CompanyShiftCatalogListResult,
  LC_ScheduleItemOut,
  LC_GetSchedulesListOut,
  ScheduleOption,
  ScheduleOptionListResult,
} from './shift.types';
import { SHIFT_REQUEST_STATUS_LABELS } from './shift.types';


/**
 * Maps backend shift request to UI model
 * Converts ISO dates to dd/MM/yyyy format and status codes to labels
 */
export const mapShiftRequestOutToModel = (dto: LC_ShiftRequestOut): ShiftRequest => {
  // Parse ISO date-time to Date object
  const parseISODate = (isoString: string): Date => {
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? new Date() : date;
  };

  // Format date to dd/MM/yyyy
  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const createdDate = parseISODate(dto.createdDate);
  const dateFromDate = parseISODate(dto.dateFrom);
  const dateToDate = dto.dateTo ? parseISODate(dto.dateTo) : null;

  return {
    id: dto.id,
    requestDate: formatDate(createdDate),
    comments: dto.requesterComment || '',
    dateFrom: formatDate(dateFromDate),
    dateTo: dateToDate ? formatDate(dateToDate) : '',
    status: dto.statusDescription || SHIFT_REQUEST_STATUS_LABELS[dto.status] || 'Desconocido',
    statusCode: dto.status,
    approverComment: dto.approvalComment || undefined,
  };
};

/**
 * Maps paginated backend response to UI page model
 */
export const mapShiftRequestPage = (dto: LC_GetShiftRequestsOut): ShiftRequestPage => ({
  data: dto.data?.map(mapShiftRequestOutToModel) || [],
  page: dto.pagination?.currentPage || 1,
  pageSize: dto.pagination?.pageSize || 10,
  totalRecords: dto.pagination?.totalRecords || 0,
  totalPages: dto.pagination?.totalPages || 0,
});

/**
 * Maps a company-scoped shift request item (admin listing) to its UI model.
 * Converts ISO dates to dd/MM/yyyy, status code to label, and formats the RUT.
 */
export const mapCompanyShiftRequestItem = (
  dto: LC_CompanyShiftRequestItem,
): CompanyShiftRequest => ({
  id: dto.id,
  employeeId: dto.employeeId,
  employeeRut: formatRut(dto.employeeRut),
  employeeName: dto.employeeName,
  requestDate: formatToDdMmYyyy(dto.requestDate),
  dateFrom: formatToDdMmYyyy(dto.dateFrom),
  dateTo: dto.dateTo ? formatToDdMmYyyy(dto.dateTo) : '',
  requesterComment: dto.requesterComment ?? '',
  status:
    dto.statusDescription ||
    SHIFT_REQUEST_STATUS_LABELS[dto.status as ShiftRequestStatus] ||
    String(dto.status),
  statusCode: dto.status,
  reviewDate: dto.reviewDate ? formatToDdMmYyyy(dto.reviewDate) : '',
  reviewerName: dto.reviewerName ?? '',
  approvalComment: dto.approvalComment ?? '',
});

/**
 * Maps the company-scoped paginated response to the UI page model.
 */
export const mapCompanyShiftRequestPage = (
  dto: LC_GetCompanyShiftRequestsOut,
): CompanyShiftRequestPage => ({
  data: dto.data?.map(mapCompanyShiftRequestItem) ?? [],
  page: dto.pagination?.currentPage ?? 1,
  pageSize: dto.pagination?.pageSize ?? 10,
  totalRecords: dto.pagination?.totalRecords ?? 0,
  totalPages: dto.pagination?.totalPages ?? 0,
});

/**
 * Maps the KPI status counters (ASIS-5237) from the dedicated counters endpoint
 * to the UI model. Defaults each counter to 0.
 */
export const mapShiftRequestCounters = (
  dto: LC_GetCompanyShiftRequestCountersOut,
): ShiftRequestCounters => ({
  pending: dto?.pending ?? 0,
  approved: dto?.approved ?? 0,
  rejected: dto?.rejected ?? 0,
});

const mapCompanyShiftCatalogItem = (dto: LC_CompanyShiftItem): CompanyShiftCatalogItem => ({
  id: dto.id,
  shiftCode: dto.shiftCode ?? '',
  shiftName: dto.shiftName ?? '',
  workingDayType: dto.workingDayType ?? '',
  workingDay: dto.workingDay ?? '',
  createdAt: formatToDdMmYyyy(dto.createdAt),
  status: dto.status ?? '',
});

/**
 * Maps the company shift catalog paginated response to the UI model.
 */
export const mapCompanyShiftCatalogList = (
  out: LC_GetCompanyShiftsListOut,
): CompanyShiftCatalogListResult => ({
  items: out.data?.map(mapCompanyShiftCatalogItem) ?? [],
  totalRecords: out.pagination?.totalRecords ?? 0,
});

const mapScheduleOption = (dto: LC_ScheduleItemOut): ScheduleOption => ({
  id: dto.scheduleId,
  name: dto.name,
  startTime: toHHmm(dto.startTime),
  endTime: toHHmm(dto.endTime),
  breakMinutes: dto.breakMinutes,
});

/**
 * Maps the company schedule catalog paginated response (ASIS-5052) to the UI model.
 */
export const mapScheduleOptions = (
  out: LC_GetSchedulesListOut,
): ScheduleOptionListResult => ({
  items: out.data?.map(mapScheduleOption) ?? [],
  totalRecords: out.pagination?.totalRecords ?? 0,
});

/**
 * Maps form data to backend submit request
 * Converts YYYY-MM-DD to ISO date-time format (YYYY-MM-DDTHH:mm:ss)
 */
export const mapFormDataToSubmitRequest = (
  formData: ShiftRequestFormData,
): LC_SubmitShiftChangeRequestIn => {
  // Convert YYYY-MM-DD to ISO date-time (start of day): YYYY-MM-DDTHH:mm:ss
  const toISODateTime = (dateString: string): string => 
    `${dateString}T00:00:00`
  ;

  return {
    requesterComment: formData.requesterComment || undefined,
    dateFrom: toISODateTime(formData.dateFrom),
    dateTo: toISODateTime(formData.dateTo),
  };
};
